// dealerProximity.ts — Coverage-Matrix Wave 2 (Squad UI-B).
// Nearby-dealer suggestions (spec §5, flag #6): when assigning a dealer to a
// territory, surface dealers situated NEAR that territory first, since a dealer
// usually serves pincodes near its base. This is a proximity RANK (soft), not a
// hard geo-fence — every dealer stays selectable, just ordered best-first.
//
// Pure — no React, no network. Ranks by the strongest locality signal available
// on each dealer (declared served pincodes → district → state), degrading
// gracefully to "unknown, keep original order" when a dealer has no location.

export interface DealerLocus {
  id: string;
  name: string;
  /** Pincodes this dealer has declared it serves (its base area). */
  pincodes?: string[] | null;
  district?: string | null;
  state?: string | null;
}

export interface TerritoryTarget {
  matchType: 'pincode' | 'city' | 'district' | 'state' | 'catchall';
  matchValue: string;
  /** The product group this territory belongs to (carried from the drilled cell). */
  productGroupKey?: string | null;
  /** Resolved parents of the drilled unit, when known (from the drill breadcrumb). */
  state?: string | null;
  district?: string | null;
}

const norm = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase();
/** Leading 3 digits of a pincode ≈ same postal region (India PIN sorting-district). */
const pinRegion = (p: string): string => p.replace(/\D/g, '').slice(0, 3);

// Lower score = nearer. Distinct tiers so ties are broken by the next-weaker signal.
const SCORE = {
  EXACT_PINCODE: 0,
  SAME_PIN_REGION: 1,
  SAME_DISTRICT: 2,
  SAME_STATE: 3,
  UNKNOWN: 9,
} as const;

/**
 * Proximity score of a dealer to a target territory. Lower = nearer.
 * Considers, in priority order: exact pincode, same 3-digit PIN region,
 * same district, same state. Returns UNKNOWN when nothing lines up.
 */
export function proximityScore(target: TerritoryTarget, dealer: DealerLocus): number {
  const tState = norm(target.state);
  const tDistrict = norm(target.district);
  const dealerPins = (dealer.pincodes ?? []).map((p) => p.replace(/\D/g, '')).filter(Boolean);
  const dealerDistrict = norm(dealer.district);
  const dealerState = norm(dealer.state);

  // Pincode-level target → strongest, pincode-based matching.
  if (target.matchType === 'pincode') {
    const tPin = target.matchValue.replace(/\D/g, '');
    if (tPin) {
      if (dealerPins.includes(tPin)) return SCORE.EXACT_PINCODE;
      if (dealerPins.some((p) => pinRegion(p) === pinRegion(tPin))) return SCORE.SAME_PIN_REGION;
    }
  }

  // District-level (or a pincode whose district we know).
  const targetDistrict = target.matchType === 'district' ? norm(target.matchValue) : tDistrict;
  if (targetDistrict && dealerDistrict && dealerDistrict === targetDistrict) return SCORE.SAME_DISTRICT;

  // State-level.
  const targetState = target.matchType === 'state' ? norm(target.matchValue) : tState;
  if (targetState && dealerState && dealerState === targetState) return SCORE.SAME_STATE;

  return SCORE.UNKNOWN;
}

/**
 * Return the dealers ranked nearest-first for the given target territory.
 * Stable: dealers with equal proximity keep their original relative order.
 * Never filters — proximity is a suggestion ranking, not a geo-fence.
 */
export function rankDealersByProximity<T extends DealerLocus>(
  target: TerritoryTarget,
  dealers: readonly T[],
): T[] {
  return dealers
    .map((d, i) => ({ d, i, s: proximityScore(target, d) }))
    .sort((a, b) => a.s - b.s || a.i - b.i)
    .map((x) => x.d);
}

/** Human label for the proximity tier — used to badge a suggested dealer row. */
export function proximityLabel(score: number): string | null {
  switch (score) {
    case SCORE.EXACT_PINCODE: return 'Serves this pincode';
    case SCORE.SAME_PIN_REGION: return 'Nearby pincode';
    case SCORE.SAME_DISTRICT: return 'Same district';
    case SCORE.SAME_STATE: return 'Same state';
    default: return null;
  }
}

export const PROXIMITY_SCORES = SCORE;
